// Google Analytics with Consent Mode v2. No analytics cookies are set until the visitor taps
// "Allow cookies"; before that GA only receives cookieless pings.
// Never sends form values, quiz answers or free text.
import { loadAdPixels, stopAdPixels, adPageView, adEvent } from './adPixels';

const KEY = 'rc_analytics_consent';
const ID = process.env.REACT_APP_GA_ID;
const CONSENT_REGIONS = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH'];
const ADS_DENIED = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
let loaded = false;
function command() { window.dataLayer.push(arguments); }
export function analyticsConsent() {
  try { return localStorage.getItem(KEY) || 'unknown'; } catch { return 'unknown'; }
}
const blocked = () => analyticsConsent() === 'denied';

// Real page URL, keeping only campaign params so no form or query data leaks.
const pageLocation = () => {
  const url = new URL(window.location.href);
  const kept = new URLSearchParams();
  url.searchParams.forEach((v, k) => { if (/^(utm_[a-z]+|gclid)$/.test(k)) kept.set(k, v); });
  const qs = kept.toString();
  return `${url.origin}${url.pathname}${qs ? `?${qs}` : ''}`;
};

export function trackFunnel(name, params = {}) {
  if (blocked()) return;
  adEvent(name, params);
  if (!loaded) return;
  if (!['page_view', 'store_click', 'app_open_click', 'pricing_click', 'quiz_start', 'quiz_complete', 'resource_click', 'sign_up', 'generate_lead'].includes(name)) return;
  const clean = {};
  for (const key of ['placement', 'destination', 'quiz_id', 'question_count', 'page_key', 'method', 'event_category', 'event_label']) {
    if (params[key] !== undefined && /^[a-z0-9_ -]{1,50}$/i.test(String(params[key]))) clean[key] = params[key];
  }
  command('event', name, { ...clean, page_location: pageLocation(), page_title: document.title, transport_type: 'beacon' });
}

function loadAnalytics() {
  if (loaded || blocked() || !ID) return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  // index.html boots gtag in <head>; static pages without that snippet boot it here.
  if (!window.__rcGtagBooted) {
    window.__rcGtagBooted = true;
    command('consent', 'default', { analytics_storage: 'denied', ...ADS_DENIED, region: CONSENT_REGIONS });
    command('consent', 'default', { analytics_storage: 'denied', ...ADS_DENIED });
    if (analyticsConsent() === 'granted') command('consent', 'update', { analytics_storage: 'granted' });
    command('js', new Date());
    command('config', ID, { allow_google_signals: false, allow_ad_personalization_signals: false, cookie_flags: 'SameSite=Lax;Secure' });
    const script = document.createElement('script');
    script.id = 'recalibrate-analytics';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ID)}`;
    document.head.appendChild(script);
  }
  // Legacy components report signup events through the same allowlist.
  window.gtag = (cmd, name, params) => { if (cmd === 'event') trackFunnel(name, params); };
}

// GA's own page_view (config) and enhanced-measurement history events count page views,
// exactly as before Sept 2026; this only makes sure the tag is loaded.
export function measurePage() {
  rememberAdLabels();
  loadAnalytics();
  adPageView();
}

export function setAnalyticsConsent(granted) {
  try { localStorage.setItem(KEY, granted ? 'granted' : 'denied'); } catch { return; }
  window[`ga-disable-${ID}`] = !granted;
  if (granted) {
    loadAnalytics();
    loadAdPixels();
    if (loaded) command('consent', 'update', { analytics_storage: 'granted' });
  } else {
    stopAdPixels();
    if (loaded) command('consent', 'update', { analytics_storage: 'denied' });
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (!/^_ga/.test(name)) return;
      const domains = [null, window.location.hostname, `.${window.location.hostname.split('.').slice(-2).join('.')}`];
      domains.forEach((domain) => { document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}; SameSite=Lax`; });
    });
  }
  window.dispatchEvent(new Event('rc-consent-changed'));
}

// Ad campaign labels (utm_*) from the landing URL are carried onto links into the web app,
// so a sign-up there is credited to the ad that brought the visitor. Labels only, no personal data.
const AD_LABELS_KEY = 'rc_ad_labels';
export function rememberAdLabels() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('utm_source')) return;
    const keep = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v && /^[a-z0-9_.-]{1,64}$/i.test(v)) keep[k] = v;
    });
    sessionStorage.setItem(AD_LABELS_KEY, JSON.stringify(keep));
  } catch { /* best effort */ }
}
function withAdLabels(url) {
  try {
    const labels = JSON.parse(sessionStorage.getItem(AD_LABELS_KEY) || 'null');
    if (!labels) return null;
    Object.entries(labels).forEach(([k, v]) => url.searchParams.set(k, v));
    return url.toString();
  } catch { return null; }
}

export function trackOutboundClick(event) {
  const anchor = event.target.closest?.('a[href]');
  if (!anchor) return;
  let url;
  try { url = new URL(anchor.href); } catch { return; }
  const appHost = new URL(process.env.REACT_APP_APP_URL).hostname;
  if (url.hostname === appHost) {
    const labelled = withAdLabels(url);
    if (labelled) anchor.href = labelled;
  }
  const fixedPlacements = ['article-top', 'article-inline', 'portal'];
  const placement = fixedPlacements.includes(anchor.dataset.appPlacement) ? anchor.dataset.appPlacement : window.location.pathname.startsWith('/portals/') ? 'portal' : window.location.pathname === '/start' ? 'quiz' : (anchor.dataset.testid || '').startsWith('hero') ? 'hero' : (anchor.dataset.testid || '').startsWith('footer') ? 'footer' : 'website';
  if (url.hostname === 'play.google.com' && url.searchParams.get('id') === 'com.recalibratepain.app') trackFunnel('store_click', { placement, destination: 'google_play' });
  else if (url.hostname === 'apps.apple.com') trackFunnel('store_click', { placement, destination: 'app_store' });
  else if (url.hostname === appHost) trackFunnel(url.pathname === '/pricing' ? 'pricing_click' : 'app_open_click', { placement, destination: 'web_app' });
}
